import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "@/portal/PortalContext";
import { toast } from "sonner";
import PortalShell from "@/portal/PortalShell";
import { PageHeader, Section, Panel, DataRow, Avatar } from "@/portal/ui/Layout";
import { Field, Input, PasswordInput, PasswordStrength } from "@/portal/ui/Field";
import { Button } from "@/portal/ui/Button";
import { fmtDateHuman } from "@/portal/format";
import { mediaUrl } from "@/lib/media";
import { Building2 } from "lucide-react";

export default function ClientAccount() {
  const { user, setUser } = useAuth();
  const { overview } = usePortal();
  const company = overview?.company;

  return (
    <PortalShell title="Contul meu">
      <PageHeader title="Contul meu" description="Datele tale de contact, compania pe care o reprezinți și securitatea contului." />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 lg:gap-14">
        <div className="space-y-12">
          <ProfileForm user={user} setUser={setUser} />
          <PasswordForm />
        </div>
        <aside className="space-y-8">
          <Section title="Compania reprezentată" description="Date administrate de echipa Brisk.">
            <Panel padded={false} className="px-5" data-testid="account-company">
              <div className="flex items-center gap-3 py-4 border-b border-cp-border">
                {company?.logo_url ? <img src={mediaUrl(company.logo_url)} alt="" className="h-11 w-11 object-contain rounded bg-white border border-cp-border p-1" />
                  : <span className="h-11 w-11 rounded bg-cp-bg-subtle grid place-items-center text-cp-text-3"><Building2 className="h-5 w-5" /></span>}
                <div className="min-w-0"><div className="text-[15px] font-semibold text-cp-text truncate">{company?.name || user?.company || "—"}</div><div className="cp-meta">Beneficiar</div></div>
              </div>
              <dl>
                {company?.address && <DataRow label="Adresă">{company.address}</DataRow>}
                {company?.contact_name && <DataRow label="Contact">{company.contact_name}</DataRow>}
                {company?.contact_email && <DataRow label="Email">{company.contact_email}</DataRow>}
                {company?.website && <DataRow label="Website"><a href={company.website.startsWith("http") ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer" className="text-cp-accent hover:underline">{company.website}</a></DataRow>}
              </dl>
            </Panel>
            <p className="cp-meta mt-2">Pentru modificări ale datelor companiei, contactează Project Managerul.</p>
          </Section>
          <Section title="Sesiune">
            <Panel padded={false} className="px-5">
              <dl>
                <DataRow label="Rol">Client</DataRow>
                <DataRow label="Termeni acceptați">{user?.terms_accepted ? "Da" : "Nu"}</DataRow>
                {user?.last_login && <DataRow label="Ultima autentificare">{fmtDateHuman(user.last_login)}</DataRow>}
              </dl>
            </Panel>
          </Section>
        </aside>
      </div>
    </PortalShell>
  );
}

function ProfileForm({ user, setUser }) {
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "", job_title: user?.job_title || "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const dirty = f.name !== (user?.name || "") || f.phone !== (user?.phone || "") || f.job_title !== (user?.job_title || "");

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!f.name.trim()) errs.name = "Introdu numele tău complet.";
    if (f.phone && !/^[+\d\s().-]{6,}$/.test(f.phone)) errs.phone = "Numărul de telefon nu pare valid.";
    setErrors(errs); if (Object.keys(errs).length) return;
    setBusy(true);
    try { const { data } = await api.put("/auth/profile", f); setUser((u) => ({ ...u, ...data })); toast.success("Datele de contact au fost salvate"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <Section title="Date de contact" description="Numele și telefonul sunt vizibile echipei Brisk care lucrează pe proiectele tale.">
      <form onSubmit={save} noValidate className="cp-panel p-5 sm:p-6" data-testid="profile-form">
        <div className="flex items-center gap-4 pb-5 mb-5 border-b border-cp-border">
          <Avatar name={f.name || user?.name} size="lg" />
          <div><div className="text-[15px] font-semibold text-cp-text">{user?.name}</div><div className="cp-meta">{user?.email}</div></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nume complet" required error={errors.name}>{(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoComplete="name" data-testid="profile-name" />}</Field>
          <Field label="Funcție" hint="Ex.: Development Director">{(p) => <Input {...p} value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} data-testid="profile-job" />}</Field>
          <Field label="Telefon" error={errors.phone}>{(p) => <Input {...p} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} autoComplete="tel" placeholder="+40 7xx xxx xxx" data-testid="profile-phone" />}</Field>
          <Field label="Email de autentificare" hint="Emailul nu poate fi schimbat din portal.">{(p) => <Input {...p} value={user?.email || ""} readOnly />}</Field>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          {dirty && <Button type="button" variant="ghost" onClick={() => { setF({ name: user?.name || "", phone: user?.phone || "", job_title: user?.job_title || "" }); setErrors({}); }}>Anulează</Button>}
          <Button type="submit" loading={busy} disabled={!dirty} data-testid="profile-save">Salvează modificările</Button>
        </div>
      </form>
    </Section>
  );
}

function PasswordForm() {
  const [f, setF] = useState({ cur: "", np: "", np2: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!f.cur) errs.cur = "Introdu parola actuală.";
    if (f.np.length < 8) errs.np = "Parola nouă trebuie să aibă cel puțin 8 caractere.";
    if (f.np && f.np2 !== f.np) errs.np2 = "Parolele nu coincid.";
    setErrors(errs); if (Object.keys(errs).length) return;
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.cur, new_password: f.np }); setF({ cur: "", np: "", np2: "" }); toast.success("Parola a fost schimbată"); }
    catch (err) { const msg = apiErr(err); setErrors(msg.toLowerCase().includes("actual") ? { cur: msg } : { np: msg }); }
    finally { setBusy(false); }
  }

  return (
    <Section title="Schimbă parola" description="Alege o parolă pe care nu o folosești în altă parte.">
      <form onSubmit={save} noValidate className="cp-panel p-5 sm:p-6" data-testid="password-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Parola actuală" required error={errors.cur} className="sm:col-span-2 sm:max-w-[calc(50%-8px)]">{(p) => <PasswordInput {...p} value={f.cur} onChange={(e) => setF({ ...f, cur: e.target.value })} autoComplete="current-password" data-testid="pwd-current" />}</Field>
          <Field label="Parola nouă" required error={errors.np}>{(p) => <><PasswordInput {...p} value={f.np} onChange={(e) => setF({ ...f, np: e.target.value })} autoComplete="new-password" data-testid="pwd-new" /><PasswordStrength value={f.np} /></>}</Field>
          <Field label="Confirmă parola nouă" required error={errors.np2}>{(p) => <PasswordInput {...p} value={f.np2} onChange={(e) => setF({ ...f, np2: e.target.value })} autoComplete="new-password" data-testid="pwd-confirm" />}</Field>
        </div>
        <div className="mt-5 flex justify-end"><Button type="submit" variant="secondary" loading={busy} data-testid="pwd-save">Actualizează parola</Button></div>
      </form>
    </Section>
  );
}
